import React, { FunctionComponent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ViewStyle } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { getBreakpointValue } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/breakpoints'

import { AuroraCareNavigatorPhotos } from '../../assets/auroraCareNavigatorAssets'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { ChevronIcon } from '../../atoms/icons/ChevronIcon'
import { Image } from '../../atoms/image/Image'
import { Link } from '../../atoms/link/Link'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title } from '../../atoms/title/Title'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface CareNavigatorCareOptionsFooterProps {
  careNavigatorLinkTestId: string
  style?: ViewStyle
  subheadTextAlign?: 'left' | 'center'
  onContactCareNavigatorPress: () => void
  headerText?: React.ReactNode
  isFavoritedProvidersPage?: boolean
}

const ContactCareNavigatorPressable = styled(PressableOpacity)<{
  isFavoritedProvidersPage?: boolean
}>(({ isFavoritedProvidersPage, theme: { colors, spacing, breakpoints, isAuroraTheme } }) => ({
  flexDirection: getBreakpointValue(breakpoints, {
    isMobileSized: isFavoritedProvidersPage ? 'column-reverse' : 'column',
    default: 'row',
  }),
  justifyContent: 'space-between',
  ...(breakpoints.isMobileSized && { alignItems: 'center', gap: spacing['16px'] }),
  ...(!isFavoritedProvidersPage && { marginBottom: spacing['24px'] }),
  ...(isAuroraTheme && {
    padding: spacing['24px'],
    backgroundColor: isFavoritedProvidersPage ? 'transparent' : colors.secondaryButtonBackgroundDefault,
    borderRadius: '16px',
    flexDirection: isFavoritedProvidersPage && breakpoints.isMobileSized ? 'column-reverse' : 'row',
  }),
}))

const ContactCareNavigatorView = styled.View<{
  isFavoritedProvidersPage?: boolean
}>(({ isFavoritedProvidersPage, theme: { colors, spacing, breakpoints, isAuroraTheme } }) => ({
  flexDirection: getBreakpointValue(breakpoints, {
    isMobileSized: isFavoritedProvidersPage ? 'column-reverse' : 'column',
    default: 'row',
  }),
  justifyContent: 'space-between',
  ...(breakpoints.isMobileSized && { alignItems: 'center', gap: spacing['16px'] }),
  ...(!isFavoritedProvidersPage && { marginBottom: spacing['24px'] }),
  ...(isAuroraTheme && {
    padding: spacing['24px'],
    backgroundColor: colors.secondaryButtonBackgroundDefault,
    borderRadius: '16px',
    flexDirection: 'row',
  }),
}))

const ContactCareNavigatorText = styled.View<{ isFavoritedProvidersPage?: boolean; theme: ThemeType }>(
  ({ isFavoritedProvidersPage, theme }) => {
    const isAuroraFavoritedMobile = theme.isAuroraTheme && isFavoritedProvidersPage && theme.breakpoints.isMobileSized
    return {
      gap: theme.spacing['8px'],
      ...(theme.breakpoints.isMobileSized && { alignItems: 'center' }),
      ...(theme.isAuroraTheme &&
        (isAuroraFavoritedMobile ? { alignItems: 'center', width: '100%' } : { alignItems: 'flex-start', flex: 1 })),
    }
  },
)

const ContactCareNavigatorImage = styled(Image)<{ isFavoritedProvidersPage?: boolean; theme: ThemeType }>(
  ({ isFavoritedProvidersPage, theme }) => ({
    width: theme.breakpoints.isMobileSized && !isFavoritedProvidersPage ? '112px' : '160px',
    height: theme.breakpoints.isMobileSized && !isFavoritedProvidersPage ? '45px' : '64px',
  }),
)

const AuroraCareNavigatorPhotosImage = styled(Image)({
  width: '160px',
  height: '61px',
})

const ChevronContainer = styled.View({
  alignItems: 'center',
  justifyContent: 'center',
})

export const CareNavigatorCareOptionsFooter: FunctionComponent<CareNavigatorCareOptionsFooterProps> = ({
  careNavigatorLinkTestId,
  style,
  subheadTextAlign = 'left',
  onContactCareNavigatorPress,
  headerText = (
    <FormattedMessage
      defaultMessage='Not sure where to start?'
      description='Header for section that takes user to contact a Care Navigator'
    />
  ),
  isFavoritedProvidersPage = false,
}) => {
  const { colors, breakpoints, isAuroraTheme } = useTheme()
  const { formatMessage } = useIntl()

  const content = (
    <>
      <ContactCareNavigatorText isFavoritedProvidersPage={isFavoritedProvidersPage}>
        <Title
          text={headerText}
          textAlign={
            breakpoints.isMobileSized && isAuroraTheme && isFavoritedProvidersPage ? 'center' : subheadTextAlign
          }
          size={TitleSize.SMALL}
          level='3'
          color={colors.textPrimary}
        />
        <BodyText
          text={
            <FormattedMessage
              defaultMessage='Our Care Navigators can help. <link>Get in touch</link>'
              description='Subtitle and link for section that takes user to contact a Care Navigator'
              values={{
                link: (chunks: string) => (
                  <Link
                    text={chunks}
                    size={BodyTextSize.SMALL}
                    onPress={onContactCareNavigatorPress}
                    underline
                    testID={tID(careNavigatorLinkTestId)}
                  />
                ),
              }}
            />
          }
          size={BodyTextSize.SMALL}
          color={colors.textSecondary}
          textAlign={breakpoints.isMobileSized && (!isAuroraTheme || isFavoritedProvidersPage) ? 'center' : 'left'}
        />
      </ContactCareNavigatorText>
      {isAuroraTheme ? (
        isFavoritedProvidersPage ? (
          <AuroraCareNavigatorPhotosImage source={AuroraCareNavigatorPhotos} accessibilityIgnoresInvertColors />
        ) : (
          <ChevronContainer>
            <ChevronIcon size={24} fillColor={colors.iconDefault} />
          </ChevronContainer>
        )
      ) : (
        <ContactCareNavigatorImage
          source={require('../../assets/care_navigator_home_base_entry_image.png')}
          accessibilityIgnoresInvertColors
          isFavoritedProvidersPage={isFavoritedProvidersPage}
        />
      )}
    </>
  )

  if (isAuroraTheme) {
    return (
      <ContactCareNavigatorPressable
        isFavoritedProvidersPage={isFavoritedProvidersPage}
        style={style}
        onPress={onContactCareNavigatorPress}
        pressedOpacity={0.8}
        accessibilityLabel={formatMessage({
          defaultMessage: 'Contact Care Navigator',
          description: 'Accessibility label for care navigator contact card',
        })}
        accessibilityRole='button'
        testID={tID('CareNavigatorCareOptionsFooter-Container')}
      >
        {content}
      </ContactCareNavigatorPressable>
    )
  }

  return (
    <ContactCareNavigatorView
      isFavoritedProvidersPage={isFavoritedProvidersPage}
      style={style}
      testID={tID('CareNavigatorCareOptionsFooter-Container')}
    >
      {content}
    </ContactCareNavigatorView>
  )
}
